/**
 * MikroTik Hybrid save → "is it on the router yet?"
 *
 * A Hybrid hEX serves prices, the portal look and printable codes from files
 * the cloud writes to it. A save marks the record dirty (`router.hexSync.dirtyAt`)
 * and the cloud writes at once; the pass that lands clean records when it
 * STARTED (`liveAt`). Until then the panel used to say nothing and the
 * operator could not tell whether the new price was on the router
 * (Ray, 2026-10-21). Same shape as portalThemeProgress: derived from record
 * timestamps, with the host's own Save instant standing in until the record
 * reflects it.
 */
export interface HexSyncState {
   at?: string | Date | null;
   dirtyAt?: string | Date | null;
   liveAt?: string | Date | null;
   error?: string | null;
}

export type RouterSyncStage = 'router-wait' | 'done' | 'stuck' | null;

/** A direct push takes seconds; after this the periodic pass (every 2 min) is the hope. */
export const STUCK_AFTER_MS = 150_000;
const SHOW_DONE_FOR_MS = 5 * 60_000;

const ms = (v: string | Date | null | undefined): number => (v ? new Date(v).getTime() || 0 : 0);

export function isHexHybrid(router: { platform?: string | null; localMode?: boolean | null } | null | undefined): boolean {
   return router?.platform === 'mikrotik' && router?.localMode === true;
}

/**
 * The sync state lives on the ROOT: a sub-vendo's page is served by its
 * parent's router, so its "live" is the parent's.
 */
export function hexSyncOf(
   device: { parent?: string | null; router?: { hexSync?: HexSyncState | null } | null } | null | undefined,
   parentRouter: { hexSync?: HexSyncState | null } | null | undefined
): HexSyncState | null {
   if (!device) return null;
   return (device.parent ? parentRouter?.hexSync : device.router?.hexSync) ?? null;
}

export function routerSyncStage(sync: HexSyncState | null | undefined, savedAt: number | null | undefined, now: number = Date.now()): RouterSyncStage {
   const anchor = Math.max(savedAt ?? 0, ms(sync?.dirtyAt));
   if (!anchor) return null;
   const elapsed = now - anchor;
   if (elapsed < 0 || elapsed > SHOW_DONE_FOR_MS) return null;
   if (ms(sync?.liveAt) >= anchor) return 'done';
   return elapsed >= STUCK_AFTER_MS ? 'stuck' : 'router-wait';
}

export function routerSyncText(stage: RouterSyncStage, elapsedMs: number, error?: string | null): string {
   switch (stage) {
      case 'router-wait': {
         const s = Math.max(0, Math.round(elapsedMs / 1000));
         return `Saved. Sending to the router… ${s}s`;
      }
      case 'done':
         return 'Live on the router. Customers see it on their next reload.';
      case 'stuck':
         return error
            ? 'Saved, but the router could not be reached. SmartFi retries every 2 minutes and will send it as soon as the router answers.'
            : 'Saved, but the router has not confirmed it yet. SmartFi retries every 2 minutes.';
      default:
         return '';
   }
}
